import React, { useState, useEffect, useRef } from 'react';
import styles from './OurStory.module.css';
import YouMayAlsoLike from '../components/YouMayAlsoLike';

const OurStory = ({ onNavigate }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const pageRef = useRef(null);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  // Story images for carousel
  const storyImages = [
    { src: '/website_images/our_story.jpg', alt: 'The Queens Hotel today' },
    { src: '/website_images/story_landlady.jpg', alt: 'Historical landlady at The Queens Hotel' },
    { src: '/website_images/story_steam.jpg', alt: 'Steam railway era in Harlech' },
    { src: '/website_images/story_hotel.jpg', alt: 'The Queens Hotel through the years' },
    { src: '/website_images/story_wedding.jpg', alt: 'Special celebrations at The Queens Hotel' }
  ];

  const nextImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === storyImages.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevImage = () => {
    setCurrentImageIndex((prevIndex) => 
      prevIndex === 0 ? storyImages.length - 1 : prevIndex - 1
    );
  };

  return (
    <div ref={pageRef} className={styles['our-story-page']}>
      <div className={styles.container}>
        <h1 className={styles['page-title']}>Our Story</h1>
        
        {/* Introduction Section */}
        <div className={styles['intro-section']}>
          <p>
            Built by ship's captain William Christall and opened in 1878, The Queens Hotel has been 
            serving visitors to Harlech for over 145 years. Strategically positioned just 80 meters 
            from Harlech railway station, our Victorian establishment was designed to welcome the 
            growing tide of tourists arriving by Great Western Railway.
          </p>
          <p>
            From its opening with Jane Christall as the first licensee, to sheltering 21 sailors 
            in 1916 after their ship foundered on Sarn Badrig, The Queens Hotel has witnessed 
            remarkable chapters of Welsh maritime and railway history while serving as a cornerstone 
            of Harlech's hospitality tradition.
          </p>
        </div>

        {/* Image Carousel */}
        <div className={styles['carousel-container']} data-testid="image-carousel">
          <button className={styles['carousel-btn']} onClick={prevImage} aria-label="Previous image">
            ‹
          </button>
          <div className={styles.carousel}>
            <img 
              src={storyImages[currentImageIndex].src}
              alt={storyImages[currentImageIndex].alt}
              className={styles['carousel-image']}
            />
          </div>
          <button className={styles['carousel-btn']} onClick={nextImage} aria-label="Next image">
            ›
          </button>
        </div>

        {/* Timeline Section */}
        <div className={styles['heritage-section']}>
          <h2 className={styles['section-title']}>Our Timeline</h2>
          <div className={styles['timeline-content']}>
            <div className={styles['timeline-item']}>
              <div className={styles['timeline-date']}>1869</div>
              <div className={styles['timeline-content-box']}>
                <h3>The Beginning</h3>
                <p>
                  Ship's captain William Christall marries Jane Jones Evans, his second wife. 
                  Before moving to Harlech, they kept a hostelry in Aberystwyth, gaining valuable 
                  experience in Welsh hospitality.
                </p>
              </div>
            </div>
            
            <div className={styles['timeline-item']}>
              <div className={styles['timeline-date']}>1878</div>
              <div className={styles['timeline-content-box']}>
                <h3>The Queens Hotel Opens</h3>
                <p>
                  William Christall builds and opens The Queens Hotel, with Jane as the first licensee. 
                  Strategically positioned near Harlech railway station to serve the growing Victorian 
                  tourism trade arriving by Great Western Railway trains.
                </p>
              </div>
            </div>
            
            <div className={styles['timeline-item']}>
              <div className={styles['timeline-date']}>1916</div>
              <div className={styles['timeline-content-box']}>
                <h3>Maritime Rescue</h3>
                <p>
                  In January, The Queens Hotel provides emergency accommodation for 21 sailors 
                  whose ship foundered on Sarn Badrig during a voyage from San Francisco to 
                  Leith, Scotland - demonstrating our long tradition of Welsh hospitality in times of need.
                </p>
              </div>
            </div>
            
            <div className={styles['timeline-item']}>
              <div className={styles['timeline-date']}>2019</div>
              <div className={styles['timeline-content-box']}>
                <h3>New Management Era</h3>
                <p>
                  Rachel and Liam move in and begin managing The Queens Hotel as renters, 
                  bringing fresh energy and vision while respecting the hotel's historic character. 
                  With three generations of the family involved in the successful running of the hotel, 
                  this marks the beginning of a new chapter in the hotel's family-run tradition.
                </p>
              </div>
            </div>
            
            <div className={styles['timeline-item']}>
              <div className={styles['timeline-date']}>Apr 2024</div>
              <div className={styles['timeline-content-box']}>
                <h3>Television Victory</h3>
                <p>
                  The Queens Hotel's "Four in a Bed" episode airs on Channel 4, with Rachel and 
                  Liam achieving a winning result. This national television exposure showcases 
                  the hotel's exceptional hospitality and puts Harlech on the map for viewers across the UK.
                </p>
              </div>
            </div>
            
            <div className={styles['timeline-item']}>
              <div className={styles['timeline-date']}>Jul 2024</div>
              <div className={styles['timeline-content-box']}>
                <h3>Proud New Owners</h3>
                <p>
                  After five years of dedicated management, Rachel and Liam complete the purchase 
                  of The Queens Hotel, becoming the proud new owners. This milestone represents 
                  the culmination of years of hard work and commitment to preserving the hotel's legacy.
                </p>
              </div>
            </div>
            
            <div className={styles['timeline-item']}>
              <div className={styles['timeline-date']}>Nov 2024</div>
              <div className={styles['timeline-content-box']}>
                <h3>Next Generation Begins</h3>
                <p>
                  Rachel's son Jago turns 18 and officially begins his hospitality journey at 
                  The Queens Hotel. With three generations now actively involved in the daily 
                  operations, the family tradition continues to strengthen and evolve.
                </p>
              </div>
            </div>
            
            <div className={styles['timeline-item']}>
              <div className={styles['timeline-date']}>Present</div>
              <div className={styles['timeline-content-box']}>
                <h3>Continuing the Legacy</h3>
                <p>
                  Over 145 years after William and Jane Christall first opened our doors, The Queens Hotel 
                  remains a family-run establishment. We continue their vision of providing warm Welsh 
                  hospitality to visitors exploring Harlech Castle and the stunning Snowdonia coastline, 
                  maintaining our Victorian heritage while embracing modern comfort.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Modern Era Section */}
        <div className={styles['modern-section']}>
          <h2 className={styles['section-title']}>Continuing the Tradition</h2>
          <div className={styles['modern-content']}>
            <p>
              Today, The Queens Hotel continues to honor its heritage while embracing the needs of 
              modern travelers. We remain committed to the same principles that have guided us for 
              generations - providing warm Welsh hospitality, comfortable accommodation, and serving 
              as a gateway to the wonders of Harlech and Snowdonia National Park.
            </p>
            <p>
              Our family-run establishment maintains the personal touch that has always defined Welsh 
              hospitality. We understand that every guest has their own story, and we're proud to 
              play a part in creating new chapters of adventure, relaxation, and discovery in this 
              UNESCO World Heritage setting.
            </p>
          </div>
        </div>

        {/* Values Section */}
        <div className={styles['values-section']}>
          <h2 className={styles['section-title']}>Our Values</h2>
          <div className={styles['values-grid']}>
            <div className={styles['value-card']}>
              <h3>Welsh Hospitality</h3>
              <p>The famous warmth and welcome of Wales runs through everything we do, from our first greeting to your fond farewell.</p>
            </div>
            
            <div className={styles['value-card']}>
              <h3>Heritage Respect</h3>
              <p>We honor our historic setting and the generations who have maintained this establishment with pride and dedication.</p>
            </div>
            
            <div className={styles['value-card']}>
              <h3>Community Connection</h3>
              <p>As a family-run business, we're deeply rooted in the Harlech community and committed to supporting local traditions and businesses.</p>
            </div>
            
            <div className={styles['value-card']}>
              <h3>Guest Experience</h3>
              <p>Every visitor to The Queens Hotel becomes part of our extended family, with personalized service and attention to detail.</p>
            </div>
          </div>
        </div>

        {/* Future Section */}
        <div className={styles['future-section']}>
          <h2 className={styles['section-title']}>Looking Forward</h2>
          <p className={styles['future-text']}>
            As we continue our journey, we remain dedicated to preserving the unique character and 
            charm that makes The Queens Hotel special, while constantly improving our facilities and 
            services. Our story is ongoing, and we look forward to welcoming you to become part of 
            our continuing narrative in this magical corner of Wales.
          </p>
        </div>
      </div>
      <YouMayAlsoLike currentPage="our-story" onNavigate={onNavigate} />
    </div>
  );
};

export default OurStory;